/**
 * Site Page Navigations Component
 *
 * Right-side navigation icons (Chat, Bookmark, Cart, Profile/SignUp).
 * Figma: Site Page Navigations (Node: 1:2892)
 *
 * All icons use lucide-react for consistent stroke weights and sizing.
 * Shared styling is defined in NavbarIconButton component.
 *
 * Container: height 31.5px, gap 14px between icons
 * Positioned on the right side of the navbar
 *
 * For anonymous users: Shows SignUpIcon instead of ProfileIcon
 */
"use client";

import React from "react";

import { Bookmark, MessagesSquare, ShoppingCart, User } from "lucide-react";

import { colors } from "@/styles/design-tokens";

import { NavbarIconButton, NAVBAR_ICON_STYLES } from "../icons/NavbarIconButton";
import { SignUpIcon } from "../icons/SignUpIcon";

interface SitePageNavigationsProps {
  bookmarkActive?: boolean;
  profileActive?: boolean;
  cartCount?: number;
  isAnonymous?: boolean;
  onChatClick?: () => void;
  onBookmarkClick?: () => void;
  onProfileClick?: () => void;
  onSignUpClick?: () => void;
  onCartClick?: () => void;
  onCartMouseEnter?: () => void;
  onCartMouseLeave?: () => void;
  cartIconRef?: React.RefObject<HTMLButtonElement | null>;
  className?: string;
}

// Cart badge styling
const BADGE_SIZE = 17;
const BADGE_OFFSET_LEFT = 16;
const BADGE_OFFSET_TOP = -3;

export function SitePageNavigations({
  bookmarkActive = false,
  profileActive = false,
  cartCount = 0,
  isAnonymous = false,
  onChatClick,
  onBookmarkClick,
  onProfileClick,
  onSignUpClick,
  onCartClick,
  onCartMouseEnter,
  onCartMouseLeave,
  cartIconRef,
  className = "",
}: SitePageNavigationsProps) {
  return (
    <div
      className={`flex items-center gap-[14px] ${className}`}
      style={{ height: `${NAVBAR_ICON_STYLES.containerSize}px` }}
    >
      {/* Chat History */}
      <NavbarIconButton icon={MessagesSquare} onClick={onChatClick} ariaLabel="Chat history" />

      {/* Bookmarks / Saved Items */}
      <NavbarIconButton
        icon={Bookmark}
        onClick={onBookmarkClick}
        active={bookmarkActive}
        ariaLabel={bookmarkActive ? "View saved items" : "Save item"}
        testId="saved-items-icon"
      />

      {/* Shopping Cart */}
      <NavbarIconButton
        ref={cartIconRef}
        icon={ShoppingCart}
        onClick={onCartClick}
        onMouseEnter={onCartMouseEnter}
        onMouseLeave={onCartMouseLeave}
        ariaLabel={`Shopping cart${cartCount > 0 ? ` with ${cartCount} items` : ""}`}
      >
        {/* Cart Badge */}
        {cartCount > 0 && (
          <div
            className="absolute flex items-center justify-center rounded-full"
            style={{
              backgroundColor: colors.stroke.strongBlack,
              width: `${BADGE_SIZE}px`,
              height: `${BADGE_SIZE}px`,
              left: `${BADGE_OFFSET_LEFT}px`,
              top: `${BADGE_OFFSET_TOP}px`,
            }}
          >
            <span className="text-caption text-white">{cartCount > 9 ? "9+" : cartCount}</span>
          </div>
        )}
      </NavbarIconButton>

      {/* Profile / Sign Up */}
      {isAnonymous ? (
        <SignUpIcon onClick={onSignUpClick} />
      ) : (
        <NavbarIconButton
          icon={User}
          onClick={onProfileClick}
          active={profileActive}
          ariaLabel="Profile"
        />
      )}
    </div>
  );
}
